import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { faqs } from '@/mocks/faqs';

export default function AboutPage() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const seoStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: '关于亲亲漫画网',
      description: '亲亲漫画网专注下拉式漫画阅读，免费下拉式看漫画，下拉式全集在线看，好看的日漫韩漫下拉式漫画大全。',
      url: 'https://qinqinmanga.com/about',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '亲亲漫画网',
      url: 'https://qinqinmanga.com',
      description: '专注下拉式漫画阅读的在线漫画平台，提供免费日漫韩漫国漫下拉式阅读体验。',
      contactPoint: {
        '@type': 'ContactPoint',
        email: 'contact@qinqinmanga.com',
        contactType: 'customer service',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://qinqinmanga.com/' },
        { '@type': 'ListItem', position: 2, name: '关于我们' },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.answer,
        },
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title="关于我们 - 亲亲漫画网"
        description="亲亲漫画网专注下拉式漫画阅读，提供免费日漫韩漫国漫下拉式全集在线阅读。了解我们的品牌故事、用户口碑和联系方式，加入500万+漫画爱好者社区。"
        keywords="亲亲漫画网,关于亲亲漫画网,下拉式漫画网,漫画阅读平台,联系我们,品牌故事"
        canonicalUrl="/about"
        structuredData={seoStructuredData}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 w-full px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Hero */}
            <section className="text-center py-10 md:py-14">
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                关于亲亲漫画网
              </h1>
              <p className="text-base md:text-lg text-foreground-500 max-w-2xl mx-auto leading-relaxed">
                专注下拉式漫画阅读，让每一部漫画都触手可及。
                我们是一群热爱漫画的年轻人，致力于为中国漫画爱好者打造最优质的下拉式阅读平台。
              </p>
            </section>

            {/* Brand Story */}
            <section className="mb-14">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div className="order-2 lg:order-1">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                    <i className="ri-bookmark-line text-primary-500"></i>
                    品牌故事
                  </h2>
                  <div className="space-y-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      亲亲漫画网诞生于对漫画的热爱。我们深知传统翻页阅读方式的痛点——加载卡顿、翻页繁琐、阅读体验割裂。于是我们决定打造一个专注于<strong>下拉式阅读</strong>的漫画平台。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      从最初的小众网站，到如今拥有500万+活跃读者的平台，亲亲漫画网始终秉持一个信念：让漫画阅读回归纯粹的快乐。我们提供最流畅的<strong>下拉式阅读体验</strong>，无需注册、无需付费，打开就能看。
                    </p>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      我们持续优化阅读引擎，确保每部漫画都能以最佳的<strong>下拉式呈现方式</strong>展现在读者面前。无论是精美的日漫分镜，还是流畅的韩漫条漫，都能在亲亲漫画网找到最适合的阅读姿态。
                    </p>
                  </div>
                </div>
                <div className="order-1 lg:order-2">
                  <div className="rounded-xl overflow-hidden">
                    <img
                      src="https://readdy.ai/api/search-image?query=A%20modern%20creative%20workspace%20with%20comic%20art%20on%20walls%2C%20warm%20lighting%2C%20designers%20working%20on%20tablets%2C%20manga%20volumes%20on%20shelves%2C%20cozy%20atmosphere%2C%20soft%20natural%20light%2C%20clean%20aesthetic%2C%20digital%20art%20studio%20vibe&width=600&height=400&seq=about-brand-01&orientation=landscape"
                      alt="亲亲漫画网品牌故事 - 漫画工作室"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-14">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 flex items-center justify-center gap-2">
                  <i className="ri-hearts-line text-primary-500"></i>
                  用户口碑
                </h2>
                <p className="text-sm text-foreground-500 mt-2">来自500万+漫画爱好者的真实评价</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-5">
                {[
                  {
                    name: '小明同学',
                    comment: '下拉式阅读真的太爽了！之前在其他网站翻页翻到手酸，亲亲漫画网一口气看完一整话，体验满分！',
                  },
                  {
                    name: '二次元少女',
                    comment: '免费的漫画网站很多，但像亲亲这样阅读体验好的真不多。更新也很快，追更党狂喜！',
                  },
                  {
                    name: '漫迷老张',
                    comment: '从网站上线就在用，见证了亲亲一步一步的成长。现在已经是我的主力看漫工具了，推荐给所有漫友。',
                  },
                  {
                    name: '追漫达人',
                    comment: '资源很全，日漫韩漫国漫都有。最喜欢下拉式这个设计，手机上看特别舒服，躺床上追漫美滋滋。',
                  },
                ].map((review, i) => (
                  <div
                    key={i}
                    className="p-5 rounded-xl bg-background-50 border border-background-200/70"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                        <i className="ri-user-3-line text-primary-500 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{review.name}</p>
                        <div className="flex items-center gap-0.5">
                          {[...Array(5)].map((_, j) => (
                            <i key={j} className="ri-star-fill text-accent-500 text-xs"></i>
                          ))}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">{review.comment}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* Contact */}
            <section className="mb-14">
              <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 md:p-8">
                <h2 className="text-xl font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-mail-send-line text-primary-500"></i>
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 mb-6">
                  有任何问题或建议？欢迎随时联系我们
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100">
                      <i className="ri-mail-line text-primary-500 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">联系邮箱</p>
                      <p className="text-sm font-medium text-foreground-800">contact@qinqinmanga.com</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-4 rounded-lg bg-background-100">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-100">
                      <i className="ri-wechat-line text-accent-600 text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">微信公众号</p>
                      <p className="text-sm font-medium text-foreground-800">亲亲漫画网</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="pb-14">
              <div className="text-center mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 flex items-center justify-center gap-2">
                  <i className="ri-question-answer-line text-primary-500"></i>
                  常见问题
                </h2>
                <p className="text-sm text-foreground-500 mt-2">快速了解亲亲漫画网的使用方式</p>
              </div>
              <div className="flex flex-col gap-3">
                {faqs.map((faq, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-background-50 border border-background-200/70 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                      className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
                    >
                      <span className="text-sm font-semibold text-foreground-900 pr-4">
                        {faq.question}
                      </span>
                      <i
                        className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 flex-shrink-0 ${
                          openFAQ === i ? 'rotate-180' : ''
                        }`}
                      ></i>
                    </button>
                    {openFAQ === i && (
                      <div className="px-5 pb-4 animate-fade-in">
                        <p className="text-sm text-foreground-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}